import { PayloadAction } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { all, put, takeEvery, takeLatest } from "redux-saga/effects";
import { BASE_URL } from "../../providers/api";
import { fileDownloadService, fileUploadService, itemFlirPhotoSaveRequestService, itemFlirPhotosListRequestService, itemPhotoSaveRequestService, itemPhotosDeleteRequestService, itemPhotosFlirDeleteRequestService, itemPhotosListRequestService, roomFlirPhotoSaveRequestService, roomFlirPhotosDeleteRequestService, roomFlirPhotosListRequestService, roomPhotoSaveRequestService, roomPhotosDeleteRequestService, roomPhotosListRequestService } from "./services";
import { blobFailure, blobRequest, blobSuccess, fileUpload, fileUploadComplete, fileUploadError, itemPhotoRemoveRequest, itemPhotoRemoveSuccess, itemPhotoSaveRequest, itemPhotoSaveSuccess, itemPhotosFailure, itemPhotosFlirFailure, itemPhotosFlirRequest, itemPhotosFlirSuccess, itemPhotosRemoveFailure, itemPhotosRequest, itemPhotosSuccess, roomFlirPhotoSaveRequest, roomFlirPhotoSaveSuccess, roomPhotoRemoveRequest, roomPhotoRemoveSuccess, roomPhotoSaveRequest, roomPhotoSaveSuccess, roomPhotosFailure, roomPhotosFlirFailure, roomPhotosFlirRequest, roomPhotosFlirSuccess, roomPhotoRemoveFailure, roomPhotosRequest, roomPhotosSuccess, roomFlirPhotoRemoveRequest, roomFlirPhotoRemoveSuccess, roomFlirPhotoRemoveFailure, itemFlirPhotoSaveSuccess, itemFlirPhotoSaveRequest, itemFlirPhotoRemoveRequest, itemFlirPhotoRemoveSuccess, itemFlirPhotosRemoveFailure } from "./slice";
import { IFlirPhotosDeleteResponse, IFlirPhotosResponse, IFlirPhotosSaveResponse, IPhotos, IPhotoSaveResponse, IPhotosDeleteResponse, IPhotosFlir, IPhotosResponse, IUploadedInfoResponse, IUploadRequest } from "./types";

// FILE UPLOAD
function* fileUploadSaga(action: PayloadAction<IUploadRequest>) {
    try {
        const uploadResponse: AxiosResponse<IUploadedInfoResponse> = yield fileUploadService(
            action.payload.id,
            action.payload.data
        );

        if (uploadResponse.data.status) {
            const [uploadedInfo] = uploadResponse.data.data;

            if (action.payload.callback && uploadedInfo) action.payload.callback({
                ...uploadedInfo,
                uploadId: action.payload.id
            })

        }
    }
    catch (error: any) {
        yield put(fileUploadError({
            uploadId: action.payload.id,
            error: {
                message: error.response.data.message || 'Error trying to upload file'
            }
        }));
    }
}

function* watchFileUploadSaga() {
    yield takeLatest(fileUpload.toString(), fileUploadSaga);
}

// BLOB DOWNLOAD
function* blobRequestSaga(action: PayloadAction<{
    link: string;
}>) {
    try {
        const fileBlob: AxiosResponse<Blob> = yield fileDownloadService(action.payload.link)

        yield put(blobSuccess({
            link: action.payload.link,
            blob: fileBlob.data
        }))
    }
    catch (error: any) {
        yield put(blobFailure({
            link: action.payload.link,
            error: {
                message: error.response.data.message || 'Error trying to download file'
            }
        }));
    }
}

function* watchBlobRequestSaga() {
    yield takeEvery(blobRequest.toString(), blobRequestSaga);
}

// ROOM PHOTOS *** LIST ***
function* roomRequestPhotosSaga(action: PayloadAction<{ roomId: string }>) {
    try {
        const photosResponse: AxiosResponse<IPhotosResponse> = yield roomPhotosListRequestService(action.payload.roomId);

        if (photosResponse.data.status) {
            const photosData: IPhotos[] = photosResponse.data.data?.map(photo => ({
                id: photo.id,
                link: `${BASE_URL}download/?type=room&id=${photo.id}`,
                dateCaptured: new Date(photo.capturedAt),
                fileName: ''
            })) || []

            yield put(roomPhotosSuccess({
                roomId: action.payload.roomId,
                data: photosData
            }));
        }
        else {
            yield put(roomPhotosFailure({
                roomId: action.payload.roomId,
                error: {
                    message: photosResponse.data.message || 'Error trying to get the list of photos'
                }
            }));
        }
    }
    catch (error) {
        yield put(roomPhotosFailure({
            roomId: action.payload.roomId,
            error: {
                message: 'Error trying to get the list of photos'
            }
        }));
    }
}

function* watchroomRequestPhotosSaga() {
    yield takeLatest(roomPhotosRequest.toString(), roomRequestPhotosSaga);
}
//ROOM PHOTOS *** SAVE ***
function* roomPhotoSaveRequestSaga(action: PayloadAction<{
    uploadId: string;
    path: string;
    roomId: string;
}>) {
    try {
        const saveResponse: AxiosResponse<IPhotoSaveResponse> = yield roomPhotoSaveRequestService(
            action.payload.roomId,
            action.payload.path
        );

        if (saveResponse.data.status) {
            const data = saveResponse.data.data;


            yield put(fileUploadComplete({ uploadId: action.payload.uploadId }));
            yield put(roomPhotoSaveSuccess({
                roomId: action.payload.roomId,
                photo: {
                    fileName: "",
                    id: data.id,
                    link: `${BASE_URL}download/?type=room&id=${data.id}`
                }
            }));
        }
    }
    catch (error: any) {
        yield put(fileUploadError({
            uploadId: action.payload.uploadId,
            error: {
                message: error.response.data.message || 'Error trying to save room photo'
            }
        }));
    }
}

function* watchRoomPhotoSaveRequestSaga() {
    yield takeLatest(roomPhotoSaveRequest.toString(), roomPhotoSaveRequestSaga);
}
//ROOM PHOTOS *** DELETE ***
function* roomDeletePhotoSaga(action: PayloadAction<{
    roomId: string,
    photoId: string
}>) {
    try {
        const photosDeleteResponse: AxiosResponse<IPhotosDeleteResponse> = yield roomPhotosDeleteRequestService(
            action.payload.photoId,
        );

        if (photosDeleteResponse.data.status) {
            yield put(roomPhotoRemoveSuccess({
                roomId: action.payload.roomId,
                photoId: action.payload.photoId
            }))
        }
        else {
            yield put(roomPhotoRemoveFailure({
                roomId: action.payload.roomId,
                photoId: action.payload.photoId,
                error: {
                    message: photosDeleteResponse.data.message || 'Error removing photo'
                }
            }));
        }
    }
    catch (error: any) {
        yield put(roomPhotoRemoveFailure({
            roomId: action.payload.roomId,
            photoId: action.payload.photoId,
            error: {
                message: error.response.data.message || 'Error trying to get the list of photos'
            }
        }));
    }
}
function* watchDeletePhotoRequestSaga() {
    yield takeLatest(roomPhotoRemoveRequest.toString(), roomDeletePhotoSaga);
}

// ROOM PHOTOS FLIR *** LIST *** 
function* roomRequestFlirPhotosSaga(action: PayloadAction<{ roomId: string }>) {
    try {
        const photosResponse: AxiosResponse<IFlirPhotosResponse> = yield roomFlirPhotosListRequestService(action.payload.roomId);

        if (photosResponse.data.status) {
            const photosData: IPhotosFlir[] = photosResponse.data.data?.map(photo => ({
                id: photo.id,
                link: `${BASE_URL}download/?type=roomflirnormal&id=${photo.id}`,
                flirLink: `${BASE_URL}download/?type=roomflirthermic&id=${photo.id}`,
                dateCaptured: new Date(photo.capturedAt),
                fileName: '',
                flirFileName: '',
            })) || []

            yield put(roomPhotosFlirSuccess({
                roomId: action.payload.roomId,
                data: photosData
            }));
        }
        else {
            yield put(roomPhotosFlirFailure({
                roomId: action.payload.roomId,
                error: {
                    message: photosResponse.data.message || 'Error trying to get the list of photos'
                }
            }));
        }
    }
    catch (error) {
        yield put(roomPhotosFlirFailure({
            roomId: action.payload.roomId,
            error: {
                message: 'Error trying to get the list of photos'
            }
        }));
    }
}

function* watchroomRequestFlirPhotosSaga() {
    yield takeLatest(roomPhotosFlirRequest.toString(), roomRequestFlirPhotosSaga);
}

//ROOM FLIR PHOTOS *** SAVE ***
function* roomPhotoFlirSaveRequestSaga(action: PayloadAction<{
    uploadNormalId: string;
    uploadFlirId: string;
    path: string;
    flirPath: string;
    roomId: string;
}>) {
    try {
        const saveResponse: AxiosResponse<IFlirPhotosSaveResponse> = yield roomFlirPhotoSaveRequestService(
            action.payload.roomId,
            action.payload.path,
            action.payload.flirPath
        );

        if (saveResponse.data.status) {
            const data = saveResponse.data.data;

            yield put(fileUploadComplete({ uploadId: action.payload.uploadNormalId }));
            yield put(fileUploadComplete({ uploadId: action.payload.uploadFlirId }));
            yield put(roomFlirPhotoSaveSuccess({
                roomId: action.payload.roomId,
                flirPhoto: {
                    fileName: "",
                    id: data.id,
                    link: `${BASE_URL}download/?type=roomflirnormal&id=${data.id}`,
                    flirLink: `${BASE_URL}download/?type=roomflirthermic&id=${data.id}`,
                    flirFileName: "",
                }
            }));
        }
    }
    catch (error: any) {
        yield put(fileUploadError({
            uploadId: action.payload.uploadNormalId || action.payload.uploadFlirId,
            error: {
                message: error.response.data.message || 'Error trying to save flir photo'
            }
        }));
    }
}

function* watchRoomFlirPhotoSaveRequestSaga() {
    yield takeLatest(roomFlirPhotoSaveRequest.toString(), roomPhotoFlirSaveRequestSaga);
}
//ROOM FLIR PHOTOS *** DELETE ***
function* roomDeleteFlirPhotoSaga(action: PayloadAction<{
    roomId: string,
    flirPhotoId: string
}>) {
    try {
        const photosDeleteResponse: AxiosResponse<IFlirPhotosDeleteResponse> = yield roomFlirPhotosDeleteRequestService(
            action.payload.flirPhotoId,
        );

        if (photosDeleteResponse.data.status) {
            yield put(roomFlirPhotoRemoveSuccess({
                flirPhotoId: action.payload.flirPhotoId,
                roomId: action.payload.roomId
            }))
        }
        else {
            yield put(roomFlirPhotoRemoveFailure({
                flirPhotoId: action.payload.flirPhotoId,
                roomId: action.payload.roomId,
                error: {
                    message: photosDeleteResponse.data.message || 'Error removing photo flir'
                }
            }));
        }
    }
    catch (error: any) {
        yield put(roomFlirPhotoRemoveFailure({
            flirPhotoId: action.payload.flirPhotoId,
            roomId: action.payload.roomId,
            error: {
                message: error.response.data.message || 'Error trying to get the list of photos'
            }
        }));
    }
}
function* watchDeleteFlirPhotoRequestSaga() {
    yield takeLatest(roomFlirPhotoRemoveRequest.toString(), roomDeleteFlirPhotoSaga);
}
// ITEM PHOTO *** LIST *** 
//function* roomPhotoFlirSaveRequestSaga(action: PayloadAction<{
//    uploadId: string;
//    path: string;
//    roomId: string;
//  }>) {
//    try {
//        const saveResponse: AxiosResponse<IPhotoSaveResponse> = yield //roomPhotoSaveRequestService(
//            action.payload.roomId,
//            action.payload.path
//       );
//
//        if(saveResponse.data.status){
//            const data = saveResponse.data.data;
//
//            yield put(fileUploadComplete({uploadId: action.payload.uploadId}));
//           yield put(roomPhotoFlirSaveSuccess({
//               roomId: action.payload.roomId,
//               photo: {
//                   fileName: "",
//                   id: data.id,
//                   link: `${MEDIA_URL}rooms/${data.roomId}/${data.path}`
//               }
//            }));
//        }
//    }
//    catch(error: any){
//        yield put(fileUploadError({
//            uploadId: action.payload.uploadId,
//            error: {
//                message: error.response.data.message || 'Error trying to save sweep room //flir photo'
//            }
//        }));
//    }
//}

//function* watchRoomPhotoFlirSaveRequestSaga() {
//    yield takeLatest(toString(), roomPhotoFlirSaveRequestSaga);
//}

// *** ITEM PHOTO ***
function* itemRequestPhotosSaga(action: PayloadAction<{ itemId: string }>) {
    try {
        const photosResponse: AxiosResponse<IPhotosResponse> = yield itemPhotosListRequestService(action.payload.itemId);

        if (photosResponse.data.status) {
            const photosData: IPhotos[] = photosResponse.data.data?.map(photo => ({
                id: photo.id,
                link: `${BASE_URL}download/?type=item&id=${photo.id}`,
                dateCaptured: new Date(photo.capturedAt),
                fileName: ''
            })) || []

            yield put(itemPhotosSuccess({
                itemId: action.payload.itemId,
                data: photosData
            }));
        }
        else {
            yield put(itemPhotosFailure({
                itemId: action.payload.itemId,
                error: {
                    message: photosResponse.data.message || 'Error trying to get the list of photos'
                }
            }));
        }
    }
    catch (error) {
        yield put(itemPhotosFailure({
            itemId: action.payload.itemId,
            error: {
                message: 'Error trying to get the list of photos'
            }
        }));
    }
}

function* watchItemRequestPhotosSaga() {
    yield takeLatest(itemPhotosRequest.toString(), itemRequestPhotosSaga);
}

//ITEM PHOTOS *** SAVE ***
function* itemPhotoSaveRequestSaga(action: PayloadAction<{
    uploadId: string;
    path: string;
    itemId: string;
}>) {
    try {
        const saveResponse: AxiosResponse<IPhotoSaveResponse> = yield itemPhotoSaveRequestService(
            action.payload.itemId,
            action.payload.path
        );

        if (saveResponse.data.status) {
            const data = saveResponse.data.data;


            yield put(fileUploadComplete({ uploadId: action.payload.uploadId }));
            yield put(itemPhotoSaveSuccess({
                itemId: action.payload.itemId,
                photo: {
                    fileName: "",
                    id: data.id,
                    link: `${BASE_URL}download/?type=item&id=${data.id}`
                }
            }));
        }
    }
    catch (error: any) {
        yield put(fileUploadError({
            uploadId: action.payload.uploadId,
            error: {
                message: error.response.data.message || 'Error trying to save item photo'
            }
        }));
    }
}

function* watchItemPhotoSaveRequestSaga() {
    yield takeLatest(itemPhotoSaveRequest.toString(), itemPhotoSaveRequestSaga);
}

//ITEM PHOTOS *** DELETE ***
function* itemDeletePhotoSaga(action: PayloadAction<{ itemId: string, photoId: string }>) {
    try {
        const photosDeleteResponse: AxiosResponse<IPhotosDeleteResponse> = yield itemPhotosDeleteRequestService(
            action.payload.photoId,
        );

        if (photosDeleteResponse.data.status) {
            yield put(itemPhotoRemoveSuccess({
                itemId: action.payload.itemId,
                photoId: action.payload.photoId
            }))
        }
        else {
            yield put(itemPhotosRemoveFailure({
                itemId: action.payload.itemId,
                photoId: action.payload.photoId,
                error: {
                    message: photosDeleteResponse.data.message || 'Error removing photo'
                }
            }));
        }
    }
    catch (error: any) {
        yield put(itemPhotosRemoveFailure({
            itemId: action.payload.itemId,
            photoId: action.payload.photoId,
            error: {
                message: error.response.data.message || 'Error trying to get the list of photos'
            }
        }));
    }
}
function* watchItemDeletePhotoRequestSaga() {
    yield takeLatest(itemPhotoRemoveRequest.toString(), itemDeletePhotoSaga);
}
// ITEM PHOTO FLIR *** LIST ***
function* itemRequestFlirPhotosSaga(action: PayloadAction<{ itemId: string }>) {
    try {
        const photosResponse: AxiosResponse<IFlirPhotosResponse> = yield itemFlirPhotosListRequestService(action.payload.itemId);

        if (photosResponse.data.status) {
            const photosData: IPhotosFlir[] = photosResponse.data.data?.map(photo => ({
                id: photo.id,
                link: `${BASE_URL}download/?type=itemflirnormal&id=${photo.id}`,
                flirLink: `${BASE_URL}download/?type=itemflirthermic&id=${photo.id}`,
                dateCaptured: new Date(photo.capturedAt),
                fileName: '',
                flirFileName: '',
            })) || []


            yield put(itemPhotosFlirSuccess({
                itemId: action.payload.itemId,
                data: photosData
            }));
        }
        else {
            yield put(itemPhotosFlirFailure({
                itemId: action.payload.itemId,
                error: {
                    message: photosResponse.data.message || 'Error trying to get the list of photos'
                }
            }));
        }
    }
    catch (error) {
        yield put(itemPhotosFlirFailure({
            itemId: action.payload.itemId,
            error: {
                message: error.response.data.message || 'Error trying to get the list of photos'
            }
        }));
    }
}

function* watchItemRequestFlirPhotosSaga() {
    yield takeLatest(itemPhotosFlirRequest.toString(), itemRequestFlirPhotosSaga);
}
//ITEM PHOTOS FLIR *** SAVE ***
function* itemFlirPhotoSaveRequestSaga(action: PayloadAction<{
    uploadNormalId: string;
    uploadFlirId: string;
    flirPath: string;
    path: string;
    itemId: string;
}>) {
    try {
        const saveResponse: AxiosResponse<IFlirPhotosSaveResponse> = yield itemFlirPhotoSaveRequestService(
            action.payload.itemId,
            action.payload.path,
            action.payload.flirPath,
        );
        if (saveResponse.data.status) {
            const data = saveResponse.data.data;


            yield put(fileUploadComplete({ uploadId: action.payload.uploadNormalId }));
            yield put(fileUploadComplete({ uploadId: action.payload.uploadFlirId }));
            yield put(itemFlirPhotoSaveSuccess({
                itemId: action.payload.itemId,
                flirPhoto: {
                    fileName: "",
                    id: data.id,
                    link: `${BASE_URL}download/?type=itemflirnormal&id=${data.id}`,
                    flirLink: `${BASE_URL}download/?type=itemflirthermic&id=${data.id}`,
                    flirFileName: "",
                }
            }));
        }
    }
    catch (error: any) {
        yield put(fileUploadError({
            uploadId: action.payload.uploadFlirId && action.payload.uploadFlirId,
            error: {
                message: error.response.data.message || 'Error trying to save item photo flir'
            }
        }));
    }
}

function* watchItemFlirPhotoSaveRequestSaga() {
    yield takeLatest(itemFlirPhotoSaveRequest.toString(), itemFlirPhotoSaveRequestSaga);
}
//ITEM FLIR PHOTOS  *** DELETE ***
function* itemDeleteFlirPhotoSaga(action: PayloadAction<{ itemId: string, flirPhotoId: string }>) {
    try {
        const photosFlirDeleteResponse: AxiosResponse<IFlirPhotosDeleteResponse> = yield itemPhotosFlirDeleteRequestService(
            action.payload.flirPhotoId,
        );

        if (photosFlirDeleteResponse.data.status) {
            yield put(itemFlirPhotoRemoveSuccess({
                itemId: action.payload.itemId,
                flirPhotoId: action.payload.flirPhotoId
            }))
        }
        else {
            yield put(itemFlirPhotosRemoveFailure({
                itemId: action.payload.itemId,
                flirPhotoId: action.payload.flirPhotoId,
                error: {
                    message: photosFlirDeleteResponse.data.message || 'Error removing photo'
                }
            }));
        }
    }
    catch (error: any) {
        yield put(itemFlirPhotosRemoveFailure({
            itemId: action.payload.itemId,
            flirPhotoId: action.payload.flirPhotoId,
            error: {
                message: error.response.data.message || 'Error trying to get the list of photos'
            }
        }));
    }
}
function* watchItemDeleteFlirPhotoRequestSaga() {
    yield takeLatest(itemFlirPhotoRemoveRequest.toString(), itemDeleteFlirPhotoSaga);
}

export default all([
    watchroomRequestPhotosSaga(),
    watchFileUploadSaga(),
    watchRoomPhotoSaveRequestSaga(),
    watchroomRequestFlirPhotosSaga(),
    watchRoomFlirPhotoSaveRequestSaga(),
    watchDeleteFlirPhotoRequestSaga(),
    watchBlobRequestSaga(),
    watchItemRequestPhotosSaga(),
    watchItemPhotoSaveRequestSaga(),
    watchItemDeletePhotoRequestSaga(),
    watchItemRequestFlirPhotosSaga(),
    watchDeletePhotoRequestSaga(),
    watchItemFlirPhotoSaveRequestSaga(),
    watchItemDeleteFlirPhotoRequestSaga(),
])